import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import CardWrapper from "../../CardWrapper";
import Card from "../../Card";
import oncoKBIcon from "../../../../pages/variant/components/OncoKB/oncoKBIcon";
import {
  handleDiagnosticLevels,
  handlePrognosticLevels,
  handleTherapeuticLevels,
  numberOfDiagnosticSvg,
  numberOfPrognosticSvg,
  numberOfTherapeuticSvg,
  oncokbTierCalculation,
} from "../../../../pages/variant/components/OncoKB/oncoKBHelpers";
import "../../../../pages/variant/components/OncoKB/oncoKB.scss";
import HoverTooltip from "../../../atoms/Tooltip/HoverTooltip";
import {
  therapeuticText,
  diagnosticText,
  prognosticText,
  oncogenicText,
} from "../../../../pages/variant/components/OncoKB/oncoKBTooltips";
import { getOncoKBData } from "../../../../state/selectors/OncoKB.selectors";
import {
  getCardsIsCompact,
  getGeneCardData,
  getUserIsPremium,
} from "../../../../state/selectors/CardsUI.selectors";

const OncoKBCard = ({
  card,
  oncoKBData,
  isCompact,
  isUserPremium,
  geneName,
}) => {
  const isDataAvailable = Array.isArray(oncoKBData) && oncoKBData.length > 0;
  const data = isDataAvailable ? oncoKBData[0] : null;

  const isDisabled = !isDataAvailable;
  const isException = !isUserPremium && !data;

  const isExceptionGene =
    geneName?.cardContent[0]?.toLowerCase() === "tp53" ||
    geneName?.cardContent[0]?.toLowerCase() === "braf" ||
    geneName?.cardContent[0]?.toLowerCase() === "ros1";

  return isException ? (
    <CardWrapper
      {...card}
      isDisabled={true}
      premiumCardMessage={
        !isExceptionGene ? (
          <div>
            <div>Only available in Premium</div>
            <div>
              Demo variants in <i>BRAF</i>, <i>TP53</i> & <i>ROS1.</i>
            </div>
          </div>
        ) : (
          "No data available"
        )
      }
    />
  ) : (
    <CardWrapper
      {...card}
      isDisabled={isDisabled}
      secondaryTitle={
        !isCompact ? (
          <HoverTooltip
            ContentRenderer={() => (
              <div className="tw-flex tw-flex-col tw-gap-3">
                {data ? (
                  <div className="tw-font-bold tw-text-center tw-text-sm">
                    Explanation for Applicable Symbols
                  </div>
                ) : null}
                {data?.oncogenic ? oncogenicText(data?.oncogenic) : null}
                {data?.highest_sensitive_level
                  ? therapeuticText(data?.highest_sensitive_level)
                  : null}
                {data?.highest_resistance_level
                  ? therapeuticText(data?.highest_resistance_level)
                  : null}
                {data?.highest_diagnostic_implication_level
                  ? diagnosticText(data?.highest_diagnostic_implication_level)
                  : null}
                {data?.highest_prognostic_implication_level
                  ? prognosticText(data?.highest_prognostic_implication_level)
                  : null}
              </div>
            )}
            delay={300}
          >
            <span>
              {data
                ? oncoKBIcon(
                    data?.oncogenic,
                    numberOfTherapeuticSvg(data?.highest_sensitive_level),
                    numberOfDiagnosticSvg(
                      data?.highest_diagnostic_implication_level
                    ),
                    numberOfTherapeuticSvg(data?.highest_resistance_level),
                    numberOfPrognosticSvg(
                      data?.highest_prognostic_implication_level
                    )
                  )
                : null}
            </span>
          </HoverTooltip>
        ) : null
      }
      color={oncokbTierCalculation(
        data?.highest_sensitive_level,
        data?.highest_diagnostic_implication_level,
        data?.highest_prognostic_implication_level,
        data?.highest_resistance_level,
        data?.highest_fda_level
      )}
    >
      <div>
        {data?.oncogenic ? <span>{data?.oncogenic}</span> : null}
        {data?.mutation_effect?.knowneffect ? (
          <span>, {data?.mutation_effect?.knowneffect}</span>
        ) : null}
      </div>
      <div>
        {data?.highest_sensitive_level ? (
          <span>{handleTherapeuticLevels(data?.highest_sensitive_level)} </span>
        ) : null}
        {data?.highest_diagnostic_implication_level ? (
          <span>
            {handleDiagnosticLevels(data?.highest_diagnostic_implication_level)}{" "}
          </span>
        ) : null}
        {data?.highest_prognostic_implication_level ? (
          <span>
            {handlePrognosticLevels(data?.highest_prognostic_implication_level)}
          </span>
        ) : null}
      </div>
    </CardWrapper>
  );
};

OncoKBCard.propTypes = {
  card: PropTypes.instanceOf(Card).isRequired,
  oncoKBData: PropTypes.arrayOf(
    PropTypes.shape({
      oncoKBData: PropTypes.shape({
        version: PropTypes.string,
        diagnostic_summary: PropTypes.string,
        gene_summary: PropTypes.string,
        highest_diagnostic_implication_level: PropTypes.string,
        highest_fda_level: PropTypes.string,
        highest_prognostic_implication_level: PropTypes.string,
        highest_resistance_level: PropTypes.string,
        highest_sensitive_level: PropTypes.string,
        oncogenic: PropTypes.string,
        query: PropTypes.shape({
          tumortype: PropTypes.string,
          alteration: PropTypes.string.isRequired,
          entrezgeneid: PropTypes.number,
          hugosymbol: PropTypes.string.isRequired,
          referencegenome: PropTypes.string,
        }),
        mutation_effect: PropTypes.shape({
          citations: PropTypes.shape({
            abstracts: PropTypes.arrayOf(PropTypes.string),
            pmids: PropTypes.arrayOf(PropTypes.string),
            description: PropTypes.string,
          }),
          description: PropTypes.string,
          knowneffect: PropTypes.string,
        }),
        treatments: PropTypes.arrayOf(
          PropTypes.shape({
            level: PropTypes.string,
            alterations: PropTypes.arrayOf(PropTypes.string),
            level_associated_cancer_type: PropTypes.shape({
              name: PropTypes.string,
            }),
            drugs: PropTypes.arrayOf(
              PropTypes.shape({
                drugName: PropTypes.string,
                description: PropTypes.string,
              })
            ),
            pmids: PropTypes.arrayOf(PropTypes.string),
          })
        ),
        diagnostic_implications: PropTypes.arrayOf(
          PropTypes.shape({
            level_of_evidence: PropTypes.string,
            alterations: PropTypes.arrayOf(PropTypes.string),
            tumor_type: PropTypes.shape({
              name: PropTypes.string,
            }),
            pmids: PropTypes.arrayOf(PropTypes.string),
          })
        ),
        prognostic_implications: PropTypes.arrayOf(
          PropTypes.shape({
            level_of_evidence: PropTypes.string,
            alterations: PropTypes.arrayOf(PropTypes.string),
            tumor_type: PropTypes.shape({
              name: PropTypes.string,
            }),
            pmids: PropTypes.arrayOf(PropTypes.string),
          })
        ),
      }),
    })
  ),
  isCompact: PropTypes.bool.isRequired,
  isUserPremium: PropTypes.bool,
  geneName: PropTypes.shape({
    cardContent: PropTypes.arrayOf(PropTypes.string),
  }),
};

OncoKBCard.defaultProps = {
  oncoKBData: [],
  isUserPremium: false,
  geneName: {},
};

const mapStateToProps = (state) => ({
  oncoKBData: getOncoKBData(state),
  isCompact: getCardsIsCompact(state),
  isUserPremium: getUserIsPremium(state),
  geneName: getGeneCardData(state),
});

export default connect(mapStateToProps)(OncoKBCard);
